import {
  APIComponentAttributes,
  APIReferenceAttributes,
  isComponent,
} from '@ardoq/api-types';
import type { EnhancedScopeData } from '@ardoq/data-model';
import { enhancedScopeDataOperations } from '@ardoq/scope-data';
import { toNumericFieldValue } from './colorRangeUtils';
import { _ComponentDefaultFilterKeys } from './filterEnums';

type SortedValues = number[];
export type SortedValuesOrNull = SortedValues | null;

/** Cache for field sorted values per scopeData snapshot, entity type, and type filter */
class ScopeDataFieldValuesCache {
  static Instance = new ScopeDataFieldValuesCache();

  static buildCacheKey(
    fieldName: string,
    isComponentType: boolean,
    typeFilter: string[] | null
  ): string {
    const typeKey = (typeFilter ?? []).toSorted().join(',');
    return `${isComponentType ? 'comp' : 'ref'}:${typeKey}:${fieldName}`;
  }

  private readonly _cache = new WeakMap<
    EnhancedScopeData,
    Map<string, SortedValuesOrNull>
  >();

  get(scopeData: EnhancedScopeData, cacheKey: string): SortedValuesOrNull {
    return this._cache.get(scopeData)?.get(cacheKey) ?? null;
  }

  has(scopeData: EnhancedScopeData, cacheKey: string): boolean {
    return this._cache.get(scopeData)?.has(cacheKey) ?? false;
  }

  set(
    scopeData: EnhancedScopeData,
    cacheKey: string,
    entry: SortedValuesOrNull
  ) {
    let scopeCache = this._cache.get(scopeData);
    if (!scopeCache) {
      scopeCache = new Map<string, SortedValuesOrNull>();
      this._cache.set(scopeData, scopeCache);
    }
    scopeCache.set(cacheKey, entry);
  }
}

const scopeDataFieldValuesCache = ScopeDataFieldValuesCache.Instance;

export const getFieldSortedValuesFromScopeData = (
  scopeData: EnhancedScopeData,
  fieldName: string,
  isComponentType: boolean,
  typeFilter: string[] | null = null
): SortedValuesOrNull => {
  const cacheKey = ScopeDataFieldValuesCache.buildCacheKey(
    fieldName,
    isComponentType,
    typeFilter
  );

  if (scopeDataFieldValuesCache.has(scopeData, cacheKey)) {
    return scopeDataFieldValuesCache.get(scopeData, cacheKey);
  }

  const entities: (APIComponentAttributes | APIReferenceAttributes)[] =
    isComponentType ? scopeData.components : scopeData.references;
  const values: number[] = [];

  for (const entity of entities) {
    if (typeFilter) {
      const entityTypeName = isComponent(entity)
        ? entity.type
        : scopeData.typesByModelId[entity.model]?.referenceTypesById[
            entity.type
          ]?.name;

      if (!entityTypeName || !typeFilter.includes(entityTypeName)) {
        continue;
      }
    }

    const rawValue =
      fieldName === _ComponentDefaultFilterKeys.INCOMING_REF_COUNT
        ? enhancedScopeDataOperations.countIncomingReferences(
            scopeData,
            entity._id
          )
        : fieldName === _ComponentDefaultFilterKeys.OUTGOING_REF_COUNT
          ? enhancedScopeDataOperations.countOutgoingReferences(
              scopeData,
              entity._id
            )
          : entity[fieldName];

    const num = toNumericFieldValue(rawValue);
    if (num !== undefined) {
      values.push(num);
    }
  }

  if (!values.length) {
    scopeDataFieldValuesCache.set(scopeData, cacheKey, null);
    return null;
  }

  const result = values.sort((a, b) => a - b);
  scopeDataFieldValuesCache.set(scopeData, cacheKey, result);
  return result;
};
